// Authentication utilities and API integration
import api from './api';

export interface User {
  id: string;
  email: string;
  name: string;
  role?: {
    id: string;
    name: string;
    permissions: string[];
  } | null;
  permissions?: string[];
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

// Local storage keys
const TOKEN_KEY = 'nuroveli_token';
const REFRESH_TOKEN_KEY = 'nuroveli_refresh_token';
const USER_KEY = 'nuroveli_user';

// Auth utility functions
export const authUtils = {
  // Store JWT token
  setToken: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  // Get JWT token
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Remove JWT token
  removeToken: (): void => {
    localStorage.removeItem(TOKEN_KEY);
  },

  // Store refresh token
  setRefreshToken: (token: string): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  // Get refresh token
  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  // Remove refresh token
  removeRefreshToken: (): void => {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  // Store user data
  setUser: (user: User): void => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  // Get user data
  getUser: (): User | null => {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  },

  // Remove user data
  removeUser: (): void => {
    localStorage.removeItem(USER_KEY);
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = authUtils.getToken();
    const user = authUtils.getUser();
    return !!(token && user);
  },

  // Clear all auth data (logout)
  clearAuth: (): void => {
    authUtils.removeToken();
    authUtils.removeRefreshToken();
    authUtils.removeUser();
  },

  // Get auth headers for API calls
  getAuthHeaders: (): Record<string, string> => {
    const token = authUtils.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
};

// Logout function - now includes backend logout
export const logout = async (): Promise<void> => {
  try {
    // Call backend logout endpoint to invalidate token
    await api.auth.logout();
  } catch (error) {
    console.error('Backend logout error:', error);
    // Continue with local logout even if backend fails
  } finally {
    // Clear authentication data
    authUtils.clearAuth();
    
    // Redirect to landing page
    window.location.href = '/';
  }
};

// Check token expiration (basic implementation)
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true; // If we can't decode, assume expired
  }
};

// Authentication API functions
export const authService = {
  // Login function
  login: async (email: string, password: string, rememberMe: boolean = false): Promise<User> => {
    const response = await api.auth.login(email, password, rememberMe);
    
    if (!response.success) {
      // Create a more informative error message
      let errorMessage = 'Login failed';
      
      if (response.errors && response.errors.length > 0) {
        // Handle validation errors
        const firstError = response.errors[0];
        if (firstError.path === 'email') {
          errorMessage = 'Please enter a valid email address';
        } else if (firstError.path === 'password') {
          errorMessage = 'Please enter your password';
        } else {
          errorMessage = firstError.msg;
        }
      } else if (response.message) {
        if (response.message.toLowerCase().includes('invalid credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (response.message.toLowerCase().includes('user not found')) {
          errorMessage = 'No account found with this email address.';
        } else {
          errorMessage = response.message;
        }
      } else if (response.error) {
        errorMessage = response.error;
      }
      
      throw new Error(errorMessage);
    }

    const { user, accessToken, refreshToken } = response.data;
    
    // Store auth data
    authUtils.setUser(user);
    authUtils.setToken(accessToken);
    if (refreshToken) {
      authUtils.setRefreshToken(refreshToken);
    }
    
    return user;
  },

  // Register function
  register: async (userData: RegisterData): Promise<User> => {
    const response = await api.auth.register(userData);
    
    if (!response.success) {
      // Create a more informative error message
      let errorMessage = 'Registration failed';
      
      if (response.errors && response.errors.length > 0) {
        // Handle validation errors
        const firstError = response.errors[0];
        if (firstError.path === 'email') {
          errorMessage = 'Please enter a valid email address';
        } else if (firstError.path === 'password') {
          errorMessage = 'Password must contain at least one lowercase letter, one uppercase letter, and one number';
        } else if (firstError.path === 'name') {
          errorMessage = 'Name must be at least 2 characters long';
        } else {
          errorMessage = firstError.msg;
        }
      } else if (response.message) {
        errorMessage = response.message;
      } else if (response.error) {
        errorMessage = response.error;
      }
      
      throw new Error(errorMessage);
    }

    const { user, accessToken, refreshToken } = response.data;
    
    // Store auth data
    authUtils.setUser(user);
    authUtils.setToken(accessToken);
    if (refreshToken) {
      authUtils.setRefreshToken(refreshToken);
    }
    
    return user;
  },

  // Refresh token function
  refreshToken: async (): Promise<string> => {
    const response = await api.auth.refreshToken();
    
    if (!response.success) {
      throw new Error(response.error || 'Token refresh failed');
    }

    const { accessToken } = response.data;
    authUtils.setToken(accessToken);
    
    return accessToken;
  },

  // Forgot password function
  forgotPassword: async (email: string): Promise<void> => {
    const response = await api.auth.forgotPassword(email);
    
    if (!response.success) {
      throw new Error(response.error || 'Password reset request failed');
    }
  },

  // Reset password function
  resetPassword: async (token: string, password: string): Promise<void> => {
    const response = await api.auth.resetPassword(token, password);
    
    if (!response.success) {
      throw new Error(response.error || 'Password reset failed');
    }
  },

  // Logout function
  logout: async (): Promise<void> => {
    const response = await api.auth.logout();
    
    if (!response.success) {
      throw new Error(response.error || 'Logout failed');
    }
  },

  // Get current user function
  getCurrentUser: async (): Promise<{ success: boolean; data?: { user: User } }> => {
    const response = await api.auth.getCurrentUser();
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to get user data');
    }

    return response;
  }
};

// Auto-logout on token expiration
export const checkTokenExpiration = (): void => {
  const token = authUtils.getToken();
  if (token && isTokenExpired(token)) {
    logout();
  }
};

// Set up periodic token check (call this in App.tsx)
export const setupTokenCheck = (): (() => void) => {
  const interval = setInterval(checkTokenExpiration, 60000); // Check every minute
  return () => clearInterval(interval);
};
